import React, { useState } from 'react';
import { X } from 'lucide-react';

const RejectionModal = ({ entity, onReject, onClose }) => {
  const [selectedType, setSelectedType] = useState(null);
  const [customReason, setCustomReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const rejectionTypes = [
    {
      type: 'hard_no',
      emoji: '❌',
      label: 'Never Show Again',
      description: 'Not a good fit for my business',
      duration: '1 year',
      color: 'red'
    },
    {
      type: 'not_now',
      emoji: '⏰',
      label: 'Not Right Now',
      description: 'Maybe later, just not today',
      duration: '1 week',
      color: 'yellow'
    },
    {
      type: 'dismiss',
      emoji: '👋',
      label: 'Just Skip',
      description: 'No specific reason',
      duration: '1 day',
      color: 'gray'
    }
  ];

  const handleSubmit = async () => {
    if (!selectedType) return;

    setIsSubmitting(true);
    try {
      await onReject(entity, selectedType, customReason);
      onClose();
    } catch (error) {
      console.error('Failed to reject entity:', error);
      alert('Failed to save rejection. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getButtonColor = (type) => {
    const colors = {
      red: 'bg-red-500 hover:bg-red-600 border-red-600',
      yellow: 'bg-yellow-500 hover:bg-yellow-600 border-yellow-600',
      gray: 'bg-gray-500 hover:bg-gray-600 border-gray-600'
    };
    return selectedType === type ? colors[rejectionTypes.find(r => r.type === type).color] : 'bg-white hover:bg-gray-50 border-gray-300';
  };

  const getTextColor = (type) => {
    return selectedType === type ? 'text-white' : 'text-gray-700';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="rejection-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            Why reject {entity.name}?
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-sm text-gray-600 mb-6">
            Help AI understand your preferences. Different rejection types have different durations.
          </p>

          {/* Rejection Type Buttons */}
          <div className="space-y-3 mb-6">
            {rejectionTypes.map((rejection) => (
              <button
                key={rejection.type}
                onClick={() => setSelectedType(rejection.type)}
                data-testid={`rejection-type-${rejection.type}`}
                className={`w-full p-4 rounded-lg border-2 transition-all ${getButtonColor(rejection.type)}`}
              >
                <div className="flex items-start text-left">
                  <span className="text-3xl mr-4">{rejection.emoji}</span>
                  <div className="flex-1">
                    <div className={`font-semibold text-lg mb-1 ${getTextColor(rejection.type)}`}>
                      {rejection.label}
                    </div>
                    <div className={`text-sm mb-2 ${getTextColor(rejection.type)}`}>
                      {rejection.description}
                    </div>
                    <div className={`text-xs ${selectedType === rejection.type ? 'text-white opacity-90' : 'text-gray-500'}`}>
                      Won't show for {rejection.duration}
                    </div>
                  </div>
                  {selectedType === rejection.type && (
                    <div className="ml-2">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Optional Custom Reason */}
          {selectedType === 'hard_no' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Optional: Tell us why (helps improve AI)
              </label>
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="e.g., Already a competitor's client, Wrong target market..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              data-testid="rejection-cancel"
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedType || isSubmitting}
              data-testid="rejection-confirm"
              className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                selectedType && !isSubmitting
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? 'Saving...' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectionModal;
